import { FunctionComponent, useMemo } from 'react'

import parse, {
  Element as DOMElement,
  DOMNode,
  domToReact,
  HTMLReactParserOptions,
} from 'html-react-parser'
import styled, { css } from 'styled-components'

import useHasInvertedColors from '@hooks/useHasInvertedColors'
import Blink from '@components/Blink'

interface HtmlViewProps {
  content: string
  allowedHtmlTags: Array<string>
  inTextBox?: boolean
  brandedBox?: boolean
}

const StyledBlink = styled(Blink)<{ hasInvertedColors: boolean }>`
  ${({
    theme: {
      colors: { staticWhite, textDefault, textBrand, staticNeutral200 },
    },
    hasInvertedColors,
  }) => css`
    color: ${hasInvertedColors ? staticWhite : textDefault};
    text-decoration: underline;

    &:hover {
      color: ${hasInvertedColors ? staticNeutral200 : textBrand};
    }
  `}
`

const ScrollableWrapper = styled.div<{ hasInvertedColors: boolean }>`
  ${({
    theme: {
      colors: { textDefault, textBrand, fillDefault, staticWhite, strokeWeak },
      spacing: { spacing12, spacing16 },
      typography: {
        subheads: {
          medium1: { bundledCSS: medium1SubheadCSS },
        },
        bodycopy: {
          small2: { bundledCSS: small2BodycopyCSS },
        },
      },
    },
    hasInvertedColors,
  }) => css`
    display: flex;
    flex-direction: row;
    white-space: nowrap;
    overflow-x: auto;
    text-align: center;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    &::-webkit-scrollbar {
      display: none;
    }

    table {
      background-color: ${fillDefault};
      border-collapse: collapse;
      width: 100%;
      border-spacing: 0 ${spacing16};

      td {
        text-align: left;
        padding: ${spacing12} 0 ${spacing12} ${spacing16};

        &:has(p) {
          padding: 0 0 0 ${spacing16};
        }
      }

      tr {
        ${medium1SubheadCSS};
        color: ${textBrand};
        white-space: initial;
        border-bottom: 1px solid ${strokeWeak};

        > :first-child {
          padding: 0;
        }
      }

      tr:not(:first-child) {
        background-color: ${fillDefault};
        padding-left: ${spacing16};
        ${small2BodycopyCSS};
        color: ${hasInvertedColors ? staticWhite : textDefault};
      }

      tr:not(:first-child) td:first-child {
        ${medium1SubheadCSS};
        padding: 0;
      }
    }
  `}
`

const HTMLView: FunctionComponent<HtmlViewProps> = ({
  content = '',
  allowedHtmlTags = [],
  inTextBox,
  brandedBox,
}) => {
  const hasInvertedColors = useHasInvertedColors()

  const options: HTMLReactParserOptions = useMemo(() => {
    return {
      replace(domNode: DOMNode) {
        if (
          domNode instanceof DOMElement &&
          !allowedHtmlTags.includes(domNode.name)
        ) {
          return (
            <span>{domToReact(domNode.children as DOMNode[], options)}</span>
          )
        }
        if (domNode instanceof DOMElement && domNode.name === 'a') {
          const { href, target, rel } = domNode.attribs

          if (!href) {
            return null
          }

          const regex = /\d+/g
          const articleId = href.match(regex)?.join('')

          return (
            <StyledBlink
              href={href}
              target={target}
              rel={rel}
              inTextBox={inTextBox}
              brandedBox={brandedBox}
              targetArticleId={articleId}
              hasInvertedColors={hasInvertedColors}
              inlineArticle
            >
              {domToReact(domNode.children as DOMNode[], options)}
            </StyledBlink>
          )
        }
        if (domNode instanceof DOMElement && domNode.name === 'table') {
          return (
            <ScrollableWrapper hasInvertedColors={hasInvertedColors}>
              <table>
                <tbody>
                  {domToReact(domNode.children as DOMNode[], {
                    replace: (domNode: DOMNode) => {
                      if (
                        domNode instanceof DOMElement &&
                        domNode.name === 'tbody'
                      ) {
                        return (
                          <>
                            {domToReact(domNode.children as DOMNode[], options)}
                          </>
                        )
                      }
                    },
                  })}
                </tbody>
              </table>
            </ScrollableWrapper>
          )
        }
        if (
          domNode instanceof DOMElement &&
          domNode.name === 'h1' &&
          domNode.children.length === 1 &&
          domNode.children[0].type === 'text'
        ) {
          const nodeTextContent = domNode.children[0].data
          return <h2>{nodeTextContent}</h2>
        }
      },
    }
  }, [hasInvertedColors, allowedHtmlTags, brandedBox, inTextBox])

  if (content === '') {
    return null
  }

  return parse(content, options)
}

export default HTMLView
